import type { Language } from '@seek/ca-myprofile-utils';
import { createForm } from '@seek/forms-ui';
import { useLanguage } from '@vocab/react';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useAnalytics } from 'src/contexts/AnalyticsContext';
import { useApiClient } from 'src/modules/ApiProvider';
import type {
  ChangeEmailStartFailure,
  ChangeEmailVerifyFailure,
} from 'src/modules/graphql/types';
import showCustomerServiceView from 'src/modules/sso/showCustomerServiceView';
import showImpersonationView from 'src/modules/sso/showImpersonationView';
import { useTypedSelector } from 'src/reducers';
import { PageUpdateActionType } from 'src/reducers/page/actionCreators';
import {
  UserActionType,
  type ChangeEmailStartModel,
  type ChangeEmailVerifyModel,
} from 'src/reducers/user/actionCreators';
import type { ServerError as ServerErrorType } from 'src/types';

import Form, { FORM_NAME } from './Form';

const { FormProvider, useField } = createForm<ChangeEmailStartModel>(
  FORM_NAME,
  {
    formType: 'braid',
  },
);
export type EmailUseField = typeof useField;

const SendCodeForm = ({
  toggle,
  setNewEmail,
  setSendCodeShown,
  setSuccess,
}: {
  toggle: () => void;
  setNewEmail: (newEmail: string) => void;
  setSendCodeShown: (state: boolean) => void;
  setSuccess: (state: boolean) => void;
}) => {
  const { language } = useLanguage();
  const dispatch = useDispatch();
  const api = useApiClient();

  const email = useTypedSelector((state) => state.user.emailAddress);
  const [customerServiceView, setCustomerServiceView] = React.useState(false);
  const [impersonationUserView, setImpersonationUserView] =
    React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = useState<ServerErrorType | null>(null);
  const { trackAccountLink } = useAnalytics();

  useEffect(() => {
    showCustomerServiceView().then(setCustomerServiceView);
  }, [setCustomerServiceView]);

  useEffect(() => {
    showImpersonationView().then(setImpersonationUserView);
  }, [setImpersonationUserView]);

  const changeEmailStartSuccessHandler = (
    formValues: ChangeEmailStartModel,
  ) => {
    // Update to new email
    dispatch({
      type: UserActionType.UPDATE_USER_STATE,
      payload: {
        emailAddress: formValues.newEmail,
      },
    });

    // Sends analytics
    dispatch({
      type: PageUpdateActionType.SET_CHANGE_EMAIL_SUCCESS,
    });

    toggle();
    setSuccess(true);
  };

  const onCallVerification = (formValues: ChangeEmailVerifyModel) => {
    setSubmitting(true);

    api
      .changeEmailVerify({ ...formValues })
      .then((result: any) => {
        if (result?.__typename === 'ChangeEmailVerifySuccess') {
          changeEmailStartSuccessHandler(formValues);
        } else if (result?.__typename === 'ChangeEmailVerifyFailure') {
          const failure = result as ChangeEmailVerifyFailure;

          let errorCode, errorMessage;

          if (failure.error.__typename === 'AccountValidationError') {
            errorCode = failure.error?.code ?? failure.error?.details[0]?.code;
            errorMessage =
              failure.error?.message ?? failure.error?.details[0]?.message;
          } else {
            errorMessage = failure.error.message;
          }
          setError({
            ...(errorCode && { errorCode }),
            ...(errorMessage && { errorMessage }),
          });
        }
        setSubmitting(false);
      })
      .catch((err: any) => {
        setError({ errorMessage: err?.message || 'Oops!' });
        setSubmitting(false);
      });

    return;
  };

  const onSubmit = (formValues: ChangeEmailStartModel) => {
    setSubmitting(true);

    trackAccountLink({
      eventName: 'request_code_pressed',
      currentPageSection: 'change email',
      passwordlessAuthentication: 'full',
      identityProvider: 'auth0',
      authenticationFlow: 'changeEmail',
    });

    api
      .changeEmailStart({ ...formValues })
      .then((result: any) => {
        if (result?.__typename === 'ChangeEmailStartSuccess') {
          if (customerServiceView) {
            // Impersonating & customer service view - does not need verification code
            // though other logics should be executed.
            // So, call to verification endpoint
            onCallVerification(formValues);
          } else if (email === formValues.newEmail && !impersonationUserView) {
            // If new email address is same as the current and not impersonating
            // no logics should be executed.
            // So, directly call the success handler
            changeEmailStartSuccessHandler(formValues);
          } else {
            // For all other cases
            // show verification screen
            setNewEmail(formValues.newEmail);
            setSendCodeShown(false);
          }
        } else if (result?.__typename === 'ChangeEmailStartFailure') {
          const failure = result as ChangeEmailStartFailure;

          let errorMessage;
          if (failure.error.__typename === 'AccountValidationError') {
            errorMessage =
              failure.error?.message ?? failure.error?.details[0]?.message;
          } else {
            errorMessage = failure.error.message;
          }
          setError({ errorMessage });
        }
        setSubmitting(false);
      })
      .catch((err: any) => {
        setError({ errorMessage: err?.message || 'Oops!' });
        setSubmitting(false);
      });

    return;
  };

  return (
    <FormProvider
      initialValues={{
        newEmail: '',
      }}
      language={language as Language}
    >
      {({ handleSubmit }) => (
        <Form
          onSubmit={handleSubmit(onSubmit)}
          submitting={submitting}
          toggle={toggle}
          error={error}
          useField={useField}
        />
      )}
    </FormProvider>
  );
};

export default SendCodeForm;
